/**
 * ClaimOrgDialog — modal for naming a clinic and optionally providing
 * a website URL for auto-fill during the gated demo conversion flow.
 */
import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import UncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import GoogleIcon from '@mui/icons-material/Google';
import { buildApiUrl } from '../../../utils/apiClient';
import { PASSWORD_RULES } from '../../../constants/passwordRules';

/* ── Password rule helpers ─────────────────────────────────────────── */

interface EvaluatedRule {
  label: string;
  met: boolean;
}

function getPasswordRules(password: string): EvaluatedRule[] {
  return PASSWORD_RULES.map((r) => ({ label: r.label, met: r.test(password) }));
}

function PasswordRuleRow({ label, met }: EvaluatedRule) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {met ? (
        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
      ) : (
        <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      )}
      <Typography
        variant="caption"
        sx={{ color: met ? 'success.main' : 'text.secondary', lineHeight: 1.4 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ── Main component ────────────────────────────────────────────────── */

/** Slug availability status returned by the parent. */
export type SlugAvailability = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

interface ClaimOrgDialogProps {
  open: boolean;
  onClose: () => void;
  clinicName: string;
  onClinicNameChange: (value: string) => void;
  slug: string;
  onSlugChange: (value: string) => void;
  slugAvailability: SlugAvailability;
  slugError: string;
  password: string;
  onPasswordChange: (value: string) => void;
  confirmPassword: string;
  onConfirmPasswordChange: (value: string) => void;
  websiteUrl: string;
  onWebsiteUrlChange: (value: string) => void;
  staffPageUrl: string;
  onStaffPageUrlChange: (value: string) => void;
  showStaffField: boolean;
  onToggleStaffField: () => void;
  /** Whether the user has accepted the HIPAA/privacy/TOS acknowledgment. */
  legalAcknowledged: boolean;
  onLegalAcknowledgedChange: (value: boolean) => void;
  claiming: boolean;
  claimError: string;
  /** Which field the error applies to — avoids fragile string-matching. */
  claimErrorField?: 'clinic' | 'password' | 'general';
  onClearError: () => void;
  onClaim: () => void;
  /** When true, the dialog is shown during demo mode (name-your-clinic step). */
  isDemoMode?: boolean;
  /** OAuth users skip the password fields (they authenticate via Google). */
  isOAuthUser?: boolean;
  /** When true the user has already completed the full consent page — hide the legal checkbox. */
  consentAlreadyCompleted?: boolean;
}

export default function ClaimOrgDialog({
  open,
  onClose,
  clinicName,
  onClinicNameChange,
  slug,
  onSlugChange,
  slugAvailability,
  slugError,
  password,
  onPasswordChange,
  confirmPassword,
  onConfirmPasswordChange,
  websiteUrl,
  onWebsiteUrlChange,
  staffPageUrl,
  onStaffPageUrlChange,
  showStaffField,
  onToggleStaffField,
  legalAcknowledged,
  onLegalAcknowledgedChange,
  claiming,
  claimError,
  claimErrorField = 'general',
  onClearError,
  onClaim,
  isDemoMode = false,
  isOAuthUser = false,
  consentAlreadyCompleted = false,
}: ClaimOrgDialogProps) {
  // Website field is always visible — we want users to enter their URL.
  const hasUrl = !!websiteUrl.trim();
  const rules = getPasswordRules(password);
  const allRulesMet = rules.every((r) => r.met);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const passwordReady = isOAuthUser || (allRulesMet && passwordsMatch);
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;
  // If the user already completed the full consent page, treat legal as satisfied.
  const legalReady = consentAlreadyCompleted || legalAcknowledged;
  // Prevent submit when slug has a known error (non-empty but not confirmed available)
  const slugHasError = slug.trim().length > 0 && (slug.trim().length < 3 || slugAvailability === 'taken' || slugAvailability === 'invalid' || slugAvailability === 'checking');

  return (
    <Dialog open={open} onClose={() => !claiming && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Name Your Clinic
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={claiming}
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isDemoMode
            ? 'Name your clinic to start customizing. We can auto-fill your practice details from your website, or you can set things up manually.'
            : 'This creates your own clinic space. All demo data stays for you to explore and customize \u2014 your changes will be saved from now on.'}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Clinic Name"
          placeholder="e.g. Sunrise Therapy Center"
          value={clinicName}
          onChange={(e) => {
            onClinicNameChange(e.target.value);
            if (claimError) onClearError();
          }}
          error={!!claimError && claimErrorField === 'clinic'}
          helperText={claimErrorField === 'clinic' ? claimError : ''}
          disabled={claiming}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && clinicName.trim() && passwordReady && !slugHasError && legalReady && !websiteUrl.trim()) onClaim();
          }}
          sx={{ mb: 2 }}
        />

        {/* URL slug — auto-generated from clinic name, editable */}
        <TextField
          fullWidth
          label="Login URL"
          placeholder="e.g. sunrise-therapy"
          value={slug}
          onChange={(e) => {
            // Enforce slug format: lowercase, alphanumeric + hyphens only
            const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
            onSlugChange(cleaned);
          }}
          disabled={claiming}
          error={slugAvailability === 'taken' || slugAvailability === 'invalid'}
          helperText={
            slugError
              ? slugError
              : slugAvailability === 'checking'
                ? 'Checking availability...'
                : slugAvailability === 'available'
                  ? 'Available! Your team will sign in at this URL.'
                  : slugAvailability === 'taken'
                    ? 'Already taken — try a different name'
                    : slugAvailability === 'invalid'
                      ? 'Must be 3-60 characters: letters, numbers, and hyphens'
                      : 'Your team\'s login page — you can change it later'
          }
          FormHelperTextProps={{
            sx: {
              color:
                slugAvailability === 'available'
                  ? 'success.main'
                  : slugAvailability === 'taken' || slugAvailability === 'invalid'
                    ? 'error.main'
                    : undefined,
              fontWeight: slugAvailability === 'available' ? 600 : undefined,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                  {window.location.host}/
                </Typography>
              </InputAdornment>
            ),
            endAdornment: slugAvailability === 'available' ? (
              <InputAdornment position="end">
                <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ mb: 2 }}
        />

        {/* Password fields — hidden for OAuth users (they authenticate via Google) */}
        {!isOAuthUser && (
          <>
            <TextField
              fullWidth
              label="Create a Password"
              type="password"
              placeholder="Enter a password"
              value={password}
              onChange={(e) => {
                onPasswordChange(e.target.value);
                if (claimError) onClearError();
              }}
              error={!!claimError && claimErrorField === 'password'}
              helperText={claimErrorField === 'password' ? claimError : ''}
              disabled={claiming}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && clinicName.trim() && passwordReady && !slugHasError && legalReady) onClaim();
              }}
              sx={{ mb: 0.5 }}
            />

            {/* Password rules — only show once the user starts typing */}
            <Collapse in={password.length > 0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5, ml: 0.5 }}>
                {rules.map((rule) => (
                  <PasswordRuleRow key={rule.label} {...rule} />
                ))}
              </Box>
            </Collapse>

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => {
                onConfirmPasswordChange(e.target.value);
                if (claimError) onClearError();
              }}
              error={showMismatch}
              helperText={
                showMismatch
                  ? 'Passwords do not match'
                  : passwordsMatch
                    ? ''
                    : "You'll use this to sign in going forward"
              }
              disabled={claiming}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && clinicName.trim() && passwordReady && !slugHasError && legalReady) onClaim();
              }}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: passwordsMatch ? (
                  <InputAdornment position="end">
                    <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </>
        )}

        {/* Website field — always visible to encourage auto-fill */}
        <TextField
          fullWidth
          label="Clinic Website"
          placeholder="e.g. www.sunrisetherapy.com"
          value={websiteUrl}
          onChange={(e) => onWebsiteUrlChange(e.target.value)}
          disabled={claiming}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LanguageIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          helperText="We'll scan your website to auto-fill staff, specialties, appointment types, and more"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && clinicName.trim() && passwordReady && !slugHasError && legalReady) onClaim();
          }}
        />
        <Collapse in={hasUrl}>
          <Box sx={{ mt: 1 }}>
            {!showStaffField ? (
              <Button
                size="small"
                onClick={onToggleStaffField}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                + Add a direct link to your staff/team page (optional)
              </Button>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Staff/Team Page URL (optional)"
                placeholder="e.g. www.sunrisetherapy.com/our-team"
                value={staffPageUrl}
                onChange={(e) => onStaffPageUrlChange(e.target.value)}
                disabled={claiming}
                helperText="If we can't find your staff automatically, paste the direct link here"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && clinicName.trim() && passwordReady && !slugHasError && legalReady) onClaim();
                }}
              />
            )}
          </Box>
        </Collapse>

        {/* General errors (server-side failures not tied to a specific field) */}
        {!!claimError && claimErrorField === 'general' && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {claimError}
          </Typography>
        )}

        {claiming && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {websiteUrl.trim()
                ? 'Creating your clinic and scanning your website...'
                : 'Creating your clinic...'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
        {/* HIPAA / Privacy / TOS acknowledgment — pinned to bottom so it's always visible */}
        {!consentAlreadyCompleted && (
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mb: 1, ml: 0 }}
            control={
              <Checkbox
                checked={legalAcknowledged}
                onChange={(e) => onLegalAcknowledgedChange(e.target.checked)}
                disabled={claiming}
                sx={{ pt: 0.25 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                I agree to the{' '}
                <MuiLink href="https://kindredly.com/terms" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                  Terms of Service
                </MuiLink>
                {' '}and{' '}
                <MuiLink href="https://kindredly.com/privacy" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                  Privacy Policy
                </MuiLink>
                , acknowledge my responsibilities on this HIPAA-conscious platform, and understand that AI-generated content must be reviewed and does not replace clinical judgment.
              </Typography>
            }
          />
        )}
        {hasUrl ? (
          /* Primary when URL provided: Autofill from Website */
          <Button
            variant="contained"
            onClick={onClaim}
            disabled={claiming || !clinicName.trim() || !passwordReady || slugHasError || !legalReady}
            startIcon={<LanguageIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {claiming ? 'Scanning & creating...' : 'Autofill from Website'}
          </Button>
        ) : (
          /* Primary when no URL: Save Clinic Name */
          <Button
            variant="contained"
            onClick={onClaim}
            disabled={claiming || !clinicName.trim() || !passwordReady || slugHasError || !legalReady}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {claiming ? 'Creating...' : 'Save Clinic Name'}
          </Button>
        )}
        {/* OAuth alternative — skip the password by signing in with Google */}
        {!isOAuthUser && (
          <>
            <Divider sx={{ width: '100%' }}>or</Divider>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={() => {
                // Persist form state so the user doesn't lose progress after OAuth redirect
                sessionStorage.setItem('claim_org_form', JSON.stringify({
                  clinicName,
                  slug,
                  websiteUrl,
                  staffPageUrl,
                  legalAcknowledged,
                }));
                window.location.href = buildApiUrl('/api/auth/login');
              }}
              disabled={claiming || !legalReady}
              sx={{ textTransform: 'none' }}
            >
              {hasUrl
                ? 'Scan my website & sign in with Google (skip password)'
                : 'Continue with Google (skip password)'}
            </Button>
          </>
        )}
        <Button
          onClick={onClose}
          disabled={claiming}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
}
